import React, { useState } from 'react';
import {
  Box,
  Flex,
  IconButton,
  Text,
  HStack,
  Input,
  Stack,
  Button,
  Spinner,
} from '@chakra-ui/react';
import { MenuRoot, MenuTrigger, MenuContent, MenuItem } from './ui/menu';
import {
  TableRoot,
  TableHeader,
  TableBody,
  TableRow,
  TableColumnHeader,
  TableCell,
} from './ui/table';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  type VisibilityState,
  type RowSelectionState,
} from '@tanstack/react-table';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { FiChevronLeft, FiChevronRight, FiSearch } from 'react-icons/fi';

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  isLoading?: boolean;
  total?: number;
  page?: number;
  perPage?: number;
  onPageChange?: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  searchPlaceholder?: string;
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  filters?: React.ReactNode;
  bulkActions?: React.ReactNode;
  enableSelection?: boolean;
  onBulkActions?: (action: string, selectedIds: string[]) => void;
  emptyMessage?: string;
  className?: string;
  selectedRowId?: string;
  getRowId?: (row: T) => string;
  getRowStyles?: (row: T) => { backgroundColor?: string; color?: string };
}

export function DataTable<T>({
  data,
  columns,
  total = 0,
  page = 1,
  perPage = 25,
  onPageChange,
  onPerPageChange,
  searchPlaceholder = 'Search...',
  globalFilter = '',
  onGlobalFilterChange,
  filters,
  bulkActions,
  enableSelection = false,
  emptyMessage = 'No data found',
  className,
  selectedRowId,
  getRowId,
  getRowStyles,
  isLoading = false,
}: DataTableProps<T>) {
  // TanStack Table state
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Create table instance
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 60,
      maxSize: 800,
    },
    manualPagination: true,
    pageCount: Math.ceil(total / perPage),
  });

  const selectedRowCount = Object.keys(rowSelection).filter(
    (key) => rowSelection[key],
  ).length;

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      maxHeight="75vh"
      minHeight="75vh"
      className={className}
    >
      {/* Filters and Search */}
      <Box mb={6} flexShrink={0}>
        <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
          <Box flex={1}>
            <HStack
              width="100%"
              bg="gray.100"
              borderRadius="xl"
              px={4}
              py={2}
              position="relative"
              height="40px"
              alignItems="center"
              minWidth="30vw"
            >
              <Input
                placeholder={searchPlaceholder}
                value={globalFilter ?? ''}
                onChange={(e) => onGlobalFilterChange?.(e.target.value)}
                fontSize="md"
                border="none"
                flex="1"
                _focus={{
                  border: 'none',
                  boxShadow: 'none',
                  outline: 'none',
                }}
                _focusVisible={{
                  border: 'none',
                  boxShadow: 'none',
                  outline: 'none',
                }}
                _placeholder={{
                  color: 'gray.500',
                }}
              />
              <IconButton
                aria-label="Search"
                size="sm"
                variant="ghost"
                color="gray.500"
                borderRadius="full"
                _hover={{ bg: 'gray.200' }}
              >
                <FiSearch />
              </IconButton>
            </HStack>
          </Box>

          {filters}
        </Stack>

        {/* Bulk Actions */}
        {enableSelection && selectedRowCount > 0 && bulkActions && (
          <Box
            mt={4}
            p={4}
            bg="blue.50"
            borderRadius="xl"
            borderWidth="1px"
            borderColor="blue.200"
          >
            <Flex justify="space-between" align="center">
              <Text fontSize="sm" fontWeight="medium" color="blue.800">
                {selectedRowCount} item{selectedRowCount > 1 ? 's' : ''}{' '}
                selected
              </Text>
              <HStack gap={2}>{bulkActions}</HStack>
            </Flex>
          </Box>
        )}
      </Box>

      {/* Table Container */}
      <Box
        flex="1"
        position="relative"
        minHeight="0" // allow the middle row to shrink and enable inner scroll
        maxHeight="calc(100vh - 300px)" // Reserve space for filters, pagination, and margins
        overflowX="auto"
        overflowY="auto"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="xl"
        bg="white"
        boxShadow="md"
        zIndex={1}
        css={{
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#cbd5e0',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a0aec0',
          },
          '& table': {
            borderCollapse: 'separate',
            borderSpacing: 0,
          },
        }}
      >
        <TableRoot
          style={{
            width: '100%',
            tableLayout: 'auto',
          }}
        >
          <TableHeader position="sticky" top={0} zIndex={50}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableColumnHeader
                    key={header.id}
                    position="sticky"
                    top={0}
                    zIndex={50}
                    bg="white"
                    borderBottomWidth="1px"
                    borderBottomColor="gray.200"
                    py={3}
                    px={3}
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.600"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    whiteSpace="nowrap"
                    cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{
                      width: `${header.getSize()}px`,
                      position: 'relative',
                    }}
                  >
                    <Flex align="center" gap={2}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                      {header.column.getCanSort() && (
                        <Box color="gray.400">
                          {{
                            asc: <FaSortUp />,
                            desc: <FaSortDown />,
                          }[header.column.getIsSorted() as string] ?? (
                            <FaSort />
                          )}
                        </Box>
                      )}
                    </Flex>
                    {header.column.getCanResize() && (
                      <Box
                        position="absolute"
                        right={0}
                        top={0}
                        height="100%"
                        width="5px"
                        cursor="col-resize"
                        userSelect="none"
                        style={{ touchAction: 'none' }}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        bg={
                          header.column.getIsResizing()
                            ? 'blue.500'
                            : 'transparent'
                        }
                        _hover={{ bg: 'gray.300' }}
                      />
                    )}
                  </TableColumnHeader>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} textAlign="center" py={6}>
                  Loading <Spinner size="sm" mr={2} />
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} textAlign="start" py={6}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                const rowId = getRowId ? getRowId(row.original) : row.id;
                const isCustomSelected = selectedRowId === rowId;
                const customStyles = getRowStyles?.(row.original) || {};

                // Determine background color priority: selected > custom > default
                let backgroundColor = 'transparent';
                if (isCustomSelected || row.getIsSelected()) {
                  backgroundColor = 'var(--chakra-colors-blue-50)';
                } else if (customStyles.backgroundColor) {
                  backgroundColor = customStyles.backgroundColor;
                }

                return (
                  <TableRow
                    key={row.id}
                    transition="background-color 0.2s"
                    _hover={{
                      backgroundColor: 'gray.50',
                    }}
                    style={{
                      backgroundColor,
                      color: customStyles.color,
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        py={3}
                        px={3}
                        style={{
                          width: `${cell.column.getSize()}px`,
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </TableRoot>
      </Box>

      {/* Pagination Controls */}
      <Box
        mt={6}
        py={4}
        px={6}
        bg="gray.50"
        borderRadius="xl"
        borderWidth="1px"
        borderColor="gray.200"
        flexShrink={0}
        boxShadow="0 -2px 8px rgba(0, 0, 0, 0.1)"
      >
        <Flex justify="space-between" align="center">
          <HStack gap={6}>
            <Text fontSize="sm" color="gray.600" fontWeight="medium">
              Total:{' '}
              <Text as="span" fontWeight="bold" color="gray.900">
                {total}
              </Text>{' '}
              items
            </Text>

            {onPerPageChange && (
              <HStack gap={3}>
                <Text fontSize="sm" color="gray.600" fontWeight="medium">
                  Rows per page:
                </Text>
                <MenuRoot>
                  <MenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      bg="white"
                      borderColor="gray.300"
                      _hover={{ borderColor: 'gray.400' }}
                    >
                      {perPage}
                      <FiChevronRight />
                    </Button>
                  </MenuTrigger>
                  <MenuContent>
                    {[10, 25, 50, 100].map((value) => (
                      <MenuItem
                        key={value}
                        value={value.toString()}
                        onClick={() => onPerPageChange(value)}
                      >
                        {value}
                      </MenuItem>
                    ))}
                  </MenuContent>
                </MenuRoot>
              </HStack>
            )}
          </HStack>

          <HStack gap={4}>
            <Text fontSize="sm" color="gray.600" fontWeight="medium">
              {total > 0
                ? `${(page - 1) * perPage + 1}-${Math.min(
                    page * perPage,
                    total,
                  )} of ${total}`
                : '0 items'}
            </Text>
            <HStack gap={1}>
              <IconButton
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => onPageChange?.(page - 1)}
                variant="outline"
                size="sm"
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400', bg: 'gray.50' }}
                _disabled={{
                  opacity: 0.4,
                  cursor: 'not-allowed',
                  _hover: { bg: 'white', borderColor: 'gray.300' },
                }}
              >
                <FiChevronLeft />
              </IconButton>
              <IconButton
                aria-label="Next page"
                disabled={page * perPage >= total}
                onClick={() => onPageChange?.(page + 1)}
                variant="outline"
                size="sm"
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400', bg: 'gray.50' }}
                _disabled={{
                  opacity: 0.4,
                  cursor: 'not-allowed',
                  _hover: { bg: 'white', borderColor: 'gray.300' },
                }}
              >
                <FiChevronRight />
              </IconButton>
            </HStack>
          </HStack>
        </Flex>
      </Box>
    </Box>
  );
}

// Export button style constants for consistency
export const dataTableButtonStyles = {
  outline: {
    variant: 'outline' as const,
    size: 'md' as const,
    borderRadius: 'xl',
    bg: 'gray.50',
    borderColor: 'gray.300',
    color: 'gray.600',
    _hover: {
      bg: 'gray.100',
      borderColor: 'gray.400',
      color: 'gray.700',
    },
    _active: {
      bg: 'gray.200',
      borderColor: 'gray.500',
    },
    transition: 'all 0.2s',
    fontWeight: 'medium',
  },
  solid: {
    variant: 'solid' as const,
    size: 'md' as const,
    borderRadius: 'xl',
    bg: 'gray.900',
    color: 'white',
    _hover: {
      bg: 'gray.800',
      transform: 'translateY(-1px)',
      boxShadow: 'lg',
    },
    _active: {
      bg: 'gray.700',
      transform: 'translateY(0)',
    },
    transition: 'all 0.2s',
    fontWeight: 'semibold',
  },
  bulkAction: {
    size: 'sm' as const,
    variant: 'outline' as const,
    borderColor: 'blue.300',
    color: 'blue.700',
    _hover: { bg: 'blue.100' },
  },
  bulkActionDanger: {
    size: 'sm' as const,
    variant: 'outline' as const,
    borderColor: 'red.300',
    color: 'red.700',
    _hover: { bg: 'red.100' },
  },
};
